import { useMutation, useQuery } from '@apollo/client';
import { isOneOf, Role } from 'auth';
import { AuthContext } from 'auth/dist/contexts';
import { User } from 'auth/dist/contexts/auth_context';
import PaginatedTable, {
  Column,
} from 'components/common/table/paginated-table';
import { hasPermissionForProgram, Permission } from 'core/permissions';
import { isNil, orderBy } from 'lodash';
import { Member } from 'models';
import * as React from 'react';
import { getData, storeParticipantsPage } from 'stores/session-storage';
import { QUERY_WITH_DIVISIONS_AND_PROGRAM } from '../../core/gql/queries/divisions-and-teams';
import AddParticipantToTeamModal from './copy-profile/copy-profile-modal';
import DeleteModal from './delete-modal/delete-participant-modal';
import EditModal from './edit-modal/edit-participant-modal';
import mapParticipantsToTable from './mapParticipantsToTable';
import './participants-list.scss';
import {
  matchesBirthdate,
  matchesDivision,
  matchesGender,
  matchesNameOrEmail,
  matchesPaymentStatus,
  matchesRegistration,
  matchesRole,
  matchesTeam,
} from './participation-filter';
import {
  birthdateFilter,
  gendersFilter,
  paymentFilter,
  rolesFilter,
} from './static-filters';
import TransferModal from './transfer-modal/transfer-participant-modal';

import { MUTATION } from '../../core/gql/mutations/delete-season-participant';
import { deleteSeasonParticipants } from './season-participants-service';

export interface Props {
  orgId: number;
  seasonId: number;
  programId: number;
  seasonData: any;
  allMembers: any;
  isLoading?: boolean;
  setMembersToExport: (data: any) => void;
  customFields?: Column[];
  setFilteredParticipantIds: (x: number[]) => void;
}

const REQUIRED_COLUMNS = [
  { name: 'name', label: 'Name', isSortable: true, style: { width: '16%' } },
  {
    name: 'gender',
    label: 'Gender',
    isSortable: true,
    selected: true,
    style: { width: '10%' },
  },
  {
    name: 'birthdate',
    label: 'Birthdate',
    isSortable: true,
    selected: true,
    style: { width: '10%' },
  },
  {
    name: 'teamDivision',
    label: 'Team/Division',
    isSortable: false,
    selected: true,
    style: { width: '20%' },
  },
  {
    name: 'registration',
    label: 'Registration',
    isSortable: false,
    selected: true,
    style: { width: '23%' },
  },
];

const sortColumn = (members: any[], sortBy: string, sortAsc: boolean) => {
  let sortFields: any = [sortBy];
  let sortOrders: any = [sortAsc ? 'asc' : 'desc'];

  if (sortBy === 'name') {
    const lastNameSorter = (member) =>
      member.lastName ? member.lastName.toLowerCase() : 0;
    const firstNameSorter = (member) =>
      member.firstName ? member.firstName.toLowerCase() : 0;
    sortFields = [lastNameSorter, firstNameSorter];
    sortOrders = [...sortOrders, ...sortOrders];
  }

  return orderBy(members, sortFields, sortOrders);
};

const itemsPerPage = 50;

const ParticipantsList = ({
  orgId,
  seasonId,
  programId,
  seasonData,
  allMembers,
  isLoading,
  setMembersToExport,
  customFields,
  setFilteredParticipantIds,
}: Props) => {
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showCopyModal, setShowCopyModal] = React.useState(false);
  const [showTransferModal, setShowTransferModal] = React.useState(false);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [actionMembers, setActionMembers] = React.useState<Member[]>([]);
  const [selectedMembers, setSelectedMembers] = React.useState<Member[]>([]);
  const [shouldClearSelectedRows, setShouldClearSelectedRows] =
    React.useState(false);
  const [filters, setFilters] = React.useState<any>({});
  const [currentPage, setCurrentPage] = React.useState(1);
  const [sortBy, setSortBy] = React.useState('name');
  const [sortAscending, setSortAscending] = React.useState(true);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [openFiltersRow, setOpenFiltersRow] = React.useState(false);
  const [columns, setColumns] = React.useState<Column[]>(REQUIRED_COLUMNS);
  const context = React.useContext(AuthContext);
  const [deleteSeasonParticipant] = useMutation(MUTATION);

  const onSelectColumns = (selectedColumns: string[]) => {
    setColumns(
      columns.map((column) =>
        column.selected === undefined
          ? column
          : { ...column, selected: selectedColumns.includes(column.name) }
      )
    );
    storeParticipantsPage({
      selectedColumns,
      filter: { ...filters, searchTerm },
    });
  };

  React.useEffect(() => {
    if (!customFields) return;

    const columnsFilteredByRole = !isOneOf(
      `${orgId}`,
      context.user,
      Role.Admin,
      Role.OrgAdmin,
      Role.ProgramAdmin,
      Role.Owner,
      Role.ProgramCoordinator
    )
      ? [
          ...REQUIRED_COLUMNS.map((x) => ({ ...x, selected: undefined })),
          {
            name: 'actions',
            label: '',
            isSortable: false,
            align: 'center',
            style: {
              width: '140px',
              alignItems: 'flex-start',
              display: 'flex',
            },
          },
        ]
      : [
          ...REQUIRED_COLUMNS,
          {
            name: 'city',
            label: 'City',
            isSortable: false,
            selected: false,
            style: { width: '10%' },
          },
          {
            name: 'state',
            label: 'State',
            isSortable: false,
            selected: false,
            style: { width: '10%' },
          },
          {
            name: 'zip',
            label: 'Zip',
            isSortable: false,
            selected: false,
            style: { width: '10%' },
          },
          {
            name: 'jerseyNumber',
            label: 'Jersey Number',
            isSortable: false,
            selected: false,
            style: { width: '10%' },
          },
          {
            name: 'position',
            label: 'Position',
            isSortable: false,
            selected: false,
            style: { width: '10%' },
          },
          {
            name: 'email',
            label: 'Email',
            isSortable: false,
            selected: false,
            style: { width: '10%' },
          },
          {
            name: 'phone',
            label: 'Phone',
            isSortable: false,
            selected: false,
            style: { width: '10%' },
          },
          ...customFields,
          {
            name: 'actions',
            label: '',
            isSortable: false,
            align: 'center',
            style: {
              width: '140px',
              alignItems: 'flex-start',
              display: 'flex',
            },
          },
        ];

    const storedFilters = getData().participants;
    setColumns(
      storedFilters?.selectedColumns && storedFilters.selectedColumns.length
        ? columnsFilteredByRole.map((x) => ({
            ...x,
            selected:
              x.selected !== undefined
                ? storedFilters.selectedColumns.includes(x.name)
                : undefined,
          }))
        : columnsFilteredByRole
    );

    if (storedFilters) {
      const birthdate = storedFilters.filter.birthdate
        ? {
            kind: storedFilters.filter.birthdate.kind,
            value:
              storedFilters.filter.birthdate.kind !== 'range'
                ? storedFilters.filter.birthdate.value
                : {
                    from: new Date(storedFilters.filter.birthdate.value.from),
                    to: new Date(storedFilters.filter.birthdate.value.to),
                  },
          }
        : undefined;

      const { searchTerm, ...filters } = storedFilters.filter;
      setFilters({ ...filters, birthdate });
      setSearchTerm(searchTerm);

      if (Object.values(filters).some((x) => !isNil(x) && x !== '')) {
        setOpenFiltersRow(true);
      }
    }
  }, [customFields]);

  const { data: divisionsData } = useQuery(QUERY_WITH_DIVISIONS_AND_PROGRAM, {
    variables: { seasonId: seasonId },
  });

  const mapData = mapParticipantsToTable(
    orgId,
    seasonId,
    programId,
    hasPermissionForProgram(
      context.user as User,
      Permission.SeasonRegistrationView,
      `${orgId}`,
      `${programId}`
    ),
    customFields || [],
    setActionMembers,
    setShowDeleteModal,
    setShowCopyModal,
    setShowTransferModal,
    setShowEditModal
  );

  const divisionsFilter = React.useMemo(() => {
    const divisionsAndProgram =
      divisionsData && divisionsData.showSeason
        ? divisionsData.showSeason
        : null;

    return PaginatedTable.SelectFilter(
      'division',
      'Division',
      divisionsAndProgram
        ? [
            {
              value: divisionsAndProgram.tsDivisionId,
              label: divisionsAndProgram.program.name,
            },
            ...divisionsAndProgram.divisions.map(({ id, name }) => ({
              value: id,
              label: name,
            })),
          ].sort((x, y) => (x.label > y.label ? 1 : -1))
        : []
    );
  }, [divisionsData]);

  const teamsFilter = React.useMemo(() => {
    const options =
      seasonData?.showSeason && seasonData?.showSeason?.teams
        ? seasonData.showSeason.teams
            .map(({ id, name, divisionId }) => {
              const division =
                divisionId && seasonData.showSeason.divisions
                  ? seasonData.showSeason.divisions.find(
                      (division) => division.id === divisionId
                    )
                  : null;

              return {
                value: id,
                label: name,
                subtext: division?.name,
              };
            })
            .sort((x, y) => (x.label > y.label ? 1 : -1))
        : [];

    options.unshift({ value: '[No Team]', label: '[No Team]' });

    return PaginatedTable.SelectFilter('team', 'Team', options);
  }, [seasonData]);

  const registrationFilter = React.useMemo(() => {
    const options =
      seasonData?.showSeason && seasonData?.showSeason?.registrationForms
        ? seasonData.showSeason.registrationForms
            .map(({ id, name, isRetired }) => ({
              value: id,
              label: `${name} ${isRetired ? '(retired)' : ''}`,
            }))
            .sort((x, y) => (x.label > y.label ? 1 : -1))
        : [];
    options.push({ value: '[No Reg Form]', label: '[No Reg Form]' });

    return PaginatedTable.SelectFilter('registration', 'Registration', options);
  }, [seasonData]);

  const whenMemberSelected = (partialMembers: { id: string }[]) => {
    const members = partialMembers.map((x) =>
      allMembers.find((y) => y.id === x.id)
    );
    setSelectedMembers(members);
  };

  const [rows, totalItems] = React.useMemo(() => {
    const startIndex = itemsPerPage * currentPage - itemsPerPage;
    const endIndex = Math.min(allMembers.length, startIndex + itemsPerPage);

    const filteredMembers = allMembers.filter(
      (x) =>
        (!searchTerm || matchesNameOrEmail(x, searchTerm)) &&
        (!filters.gender || matchesGender(x, filters.gender)) &&
        (!filters.team || matchesTeam(x, filters.team)) &&
        (!filters.role || matchesRole(x, filters.role)) &&
        (!filters.division || matchesDivision(x, filters.division)) &&
        (!filters.registration ||
          matchesRegistration(x, filters.registration)) &&
        (!filters.birthdate || matchesBirthdate(x, filters.birthdate)) &&
        (!filters.payment || matchesPaymentStatus(x, filters.payment))
    );

    setFilteredParticipantIds(
      filteredMembers.flatMap((x) =>
        x.seasonParticipants.map((y) => y.seasonParticipantId)
      )
    );
    if (filteredMembers.length) {
      setMembersToExport(filteredMembers);
    }

    const totalItems = filteredMembers.length;
    const sortedMembers = sortColumn(filteredMembers, sortBy, sortAscending);
    const paginatedMembers = sortedMembers.slice(startIndex, endIndex);

    const rows = paginatedMembers.map(mapData);

    return [rows, totalItems];
  }, [
    searchTerm,
    filters,
    sortBy,
    sortAscending,
    allMembers,
    currentPage,
    mapData,
    setMembersToExport,
    setFilteredParticipantIds,
  ]);

  return (
    <>
      <div className="u-padBottomXxl participants-list">
        {actionMembers.length > 0 ? (
          <DeleteModal
            show={showDeleteModal}
            setShow={setShowDeleteModal}
            members={actionMembers}
            whenFinishDelete={() => setShouldClearSelectedRows(true)}
            onDelete={async (membersToDelete) => {
              await deleteSeasonParticipants(
                deleteSeasonParticipant,
                orgId,
                membersToDelete
              );
            }}
          />
        ) : null}

        {actionMembers.length > 0 ? (
          <AddParticipantToTeamModal
            show={showCopyModal}
            setShow={setShowCopyModal}
            orgId={orgId}
            members={actionMembers}
            contextSeasonId={seasonId}
            whenFinishCopy={() => setShouldClearSelectedRows(true)}
          />
        ) : null}

        {actionMembers.length > 0 ? (
          <TransferModal
            show={showTransferModal}
            setShow={setShowTransferModal}
            orgId={orgId}
            member={actionMembers[0]}
            contextSeasonId={seasonId}
          />
        ) : null}

        {actionMembers.length > 0 ? (
          <EditModal
            show={showEditModal}
            setShow={setShowEditModal}
            member={actionMembers[0]}
          />
        ) : null}

        <PaginatedTable
          tableId="participants-table"
          sorting={{ sortBy, sortAscending, setSortBy, setSortAscending }}
          pagination={{ totalItems, itemsPerPage, currentPage, setCurrentPage }}
          searchTerm={searchTerm}
          setSearchTerm={(term) => {
            storeParticipantsPage({
              filter: { ...filters, searchTerm: term },
              selectedColumns: columns
                .filter((x) => x.selected)
                .map((x) => x.name),
            });
            setSearchTerm(term);
          }}
          rows={rows}
          openFiltersRow={openFiltersRow}
          activeFilters={filters}
          setActiveFilters={(currentFilters) => {
            storeParticipantsPage({
              filter: { ...currentFilters, searchTerm },
              selectedColumns: columns
                .filter((x) => x.selected)
                .map((x) => x.name),
            });
            setFilters(currentFilters);
          }}
          isLoading={isLoading && !allMembers.length}
          onSelectRows={whenMemberSelected}
          searchPlaceholder="Search by member name or email..."
          shouldClearSelectedRows={shouldClearSelectedRows}
          onSelectColumns={onSelectColumns}
          columns={columns}
          filters={[
            rolesFilter,
            gendersFilter,
            birthdateFilter,
            divisionsFilter,
            teamsFilter,
            registrationFilter,
            paymentFilter,
          ]}
          bulkActions={[
            {
              label: 'Delete',
              disabled: selectedMembers.length === 0,
              onSelected: () => {
                setShouldClearSelectedRows(false);
                setActionMembers(selectedMembers);
                setShowDeleteModal(true);
              },
            },
            {
              label: 'Copy Profiles',
              disabled: selectedMembers.length === 0,
              onSelected: () => {
                setShouldClearSelectedRows(false);
                setActionMembers(selectedMembers);
                setShowCopyModal(true);
              },
            },
          ]}
        />
      </div>
    </>
  );
};

ParticipantsList.defaultProps = {
  isLoading: false,
};

export default React.memo(ParticipantsList);
